import api from '../redux/features/auth/axiosAuthHelper';
import { apiError, type ApiError } from './apiError';

export type ExportFormat = 'csv' | 'xlsx';
export type ExportMode = 'selected' | 'all_matching';
export interface ExportOptions { format: ExportFormat; mode: ExportMode; selectedIds: string[] }

class InvalidDownload extends Error {}

export function exportQuery(params: URLSearchParams): string {
  const query = new URLSearchParams(params);
  query.delete('limit'); query.delete('offset'); query.delete('format');
  // Exports always identify the display timezone, even without a date filter.
  query.set('timezone', Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC');
  return query.toString();
}

export function downloadFilename(header: string | undefined, format: ExportFormat, identity: string): string {
  const star = header?.match(/filename\*=UTF-8''([^;]+)/i);
  const plain = header?.match(/filename="([^"]+)"|filename=([^;]+)/i);
  let name: string | undefined;
  try { name = star ? decodeURIComponent(star[1]) : plain?.[1] ?? plain?.[2]?.trim(); }
  catch { throw new InvalidDownload(); }
  if (!name) name = `${identity}_${new Date().toISOString().replace(/[:.]/g, '-')}.${format}`;
  name = name.replace(/[\\/:\x00-\x1f]/g, '_');
  if (!name.toLowerCase().endsWith(`.${format}`)) throw new InvalidDownload();
  return name;
}

/** Keep the synchronous browser resource lifecycle together after validation. */
function downloadBlob(blob: Blob, name: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  try {
    link.href = url; link.download = name;
    document.body.appendChild(link); link.click();
  } finally {
    link.remove();
    // Let the browser start consuming the download before releasing its URL.
    window.setTimeout(() => {
      URL.revokeObjectURL(url);
    }, 1000);
  }
}

export async function requestExport(endpoint: string, query: string, options: ExportOptions, signal: AbortSignal): Promise<void> {
  const payload = { format: options.format, mode: options.mode,
    ...(options.mode === 'selected' ? { selected_ids: options.selectedIds } : {}) };
  const response = await api.post<Blob>(`${endpoint}/export/?${query}`, payload, { responseType: 'blob', signal, timeout: 45000 });
  if (signal.aborted) return;
  const contentType = String(response.headers['content-type'] ?? '').split(';')[0].trim().toLowerCase();
  const expectedType = options.format === 'csv' ? 'text/csv' : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
  if (!(response.data instanceof Blob) || !response.data.size || response.data.size > 20 * 1024 * 1024 || contentType !== expectedType) {
    throw new InvalidDownload();
  }
  const identity = { users: 'UserList', skus: 'SkuList', devices: 'APIConfigurationsList', 'audit-logs': 'AuditList' }[endpoint] ?? 'Export';
  const name = downloadFilename(response.headers['content-disposition'], options.format, identity);
  downloadBlob(response.data, name);
}

async function blobJson(blob: Blob): Promise<unknown> {
  if (blob.size > 65536) return undefined;
  const text = await new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = reject;
    reader.readAsText(blob);
  });
  return JSON.parse(text);
}

export async function exportError(failure: unknown): Promise<ApiError | null> {
  if (failure instanceof InvalidDownload) return { fields: {}, message: 'The server did not return a valid export file. Please try again.' };
  const error = failure as { code?: string; response?: { status?: number; data?: unknown; headers?: Record<string, unknown> } };
  const status = error?.response?.status;
  if (status === 400 || status === 413) {
    let data = error.response?.data;
    if (data instanceof Blob) {
      try { data = await blobJson(data); } catch { data = undefined; }
    }
    const messages: string[] = [];
    const collect = (value: unknown, depth = 0) => {
      if (depth > 3 || messages.length >= 5) return;
      if (typeof value === 'string') messages.push(value.slice(0, 300));
      else if (Array.isArray(value)) value.forEach(item => collect(item, depth + 1));
      else if (value && typeof value === 'object') Object.values(value).forEach(item => collect(item, depth + 1));
    };
    if (data && typeof data === 'object') collect(data);
    return { status, fields: {}, message: messages.join(' ') || 'The export request was not accepted. Check the selection and filters, then try again.' };
  }
  const result = apiError(failure);
  if (!result) return null;
  if (status === 403) result.message = 'You do not have permission to export these records.';
  else if (error?.code === 'ECONNABORTED') result.message = 'The export timed out. Narrow the filters and try again.';
  else if (status && status !== 401 && status !== 429) result.message = 'The export could not be generated. Please try again.';
  return result;
}
